/**
 * FirmProvider — loads the firm's branding once for the whole authed portal, so
 * the shell can wear the firm's logo and accent and the branding screen can edit
 * them and re-read the result.
 *
 * Mounted INSIDE the authed area (under RequireAdvisor), so it only ever runs
 * with a valid advisor session.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { getFirm, type FirmBranding } from './firmApi';

interface FirmContextValue {
  firm: FirmBranding | null;
  loading: boolean;
  reload: () => Promise<void>;
}

const FirmContext = createContext<FirmContextValue | null>(null);

export function useFirm(): FirmContextValue {
  const v = useContext(FirmContext);
  if (v === null) throw new Error('useFirm must be used within <FirmProvider>');
  return v;
}

export function FirmProvider({ children }: { children: ReactNode }) {
  const { api } = useAuth();
  const [firm, setFirm] = useState<FirmBranding | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      setFirm(await getFirm(api));
    } catch {
      // A firm that can't be loaded shouldn't break the portal — the shell just
      // falls back to the house brand. Real advisor routes surface their own errors.
      setFirm(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return <FirmContext.Provider value={{ firm, loading, reload }}>{children}</FirmContext.Provider>;
}
